///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { GtthNodeFeatureDtoModel } from '../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthImportNodeTypeEnum } from './import-node-type.enum';
import { GtthPosition } from '../../logical-topology/entities/position';
import { GtthCrudMultipleEntitiesCommand } from '../../logical-topology/engine/commands/crud-multiple-entities-command';
import { GtthDuplicatedNodeFeatureDto } from './node-duplications-resolver/model/duplicated-node-feature-dto.model';
import { DuplicateTypeEnum } from './node-duplications-resolver/duplicate-type-enum';
import { BehaviorSubject } from 'rxjs';
import { GtthLogicalTopology } from '../../logical-topology/entities/logical-topology';
import { StringDistance } from '../../../services/string_distance.service';
import { NodeFeature } from '../../logical-topology/entities/node/node-feature';
import { LinkFeature } from '../../logical-topology/entities/link/link-feature';

@Injectable()
export class GtthImportNodesService {
    public editingLocker = new BehaviorSubject<GtthDuplicatedNodeFeatureDto>(null);
    public nodeNameSynchronizer = new BehaviorSubject<GtthDuplicatedNodeFeatureDto>(null);
    public nodeTypeSynchronizer = new BehaviorSubject<GtthDuplicatedNodeFeatureDto>(null);
    public linksMap: Map<number, GtthLinkFeatureDtoModel[]>;
    public losesToRemove: Set<number>;
    public createLinksCount: number;
    public createLosesCount: number;
    public hasNodesWithLayers = false;
    private importedNodesMap: Map<number, GtthNodeFeatureDtoModel>;
    private importedLinks: GtthLinkFeatureDtoModel[];
    private nodesMap: Map<GtthImportNodeTypeEnum, GtthNodeFeatureDtoModel[]>;
    private nodeTypesMap = new Map<number, number>();
    private nodesDuplicationMap: Map<number, GtthDuplicatedNodeFeatureDto[]>;
    private readonly fileDuplications: GtthDuplicatedNodeFeatureDto[][] = [];
    private movedConnectedNodes = new Set<number>();

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly stringDistance: StringDistance
    ) {}

    clearAll() {
        this.importedNodesMap = new Map();
        this.importedLinks = [];
    }

    handleImportedNodes(importedNodes: GtthNodeFeatureDtoModel[], importedLinks: GtthLinkFeatureDtoModel[]) {
        this.importedLinks = importedLinks;
        this.importedNodesMap = new Map();
        importedNodes.forEach((node) => this.importedNodesMap.set(node.getId(), node));
        this.hasNodesWithLayers = importedNodes.some((node) => node.getLayerIndex() !== GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);
    }

    hasDuplicateRelatedToSameOrigin(editedNode: GtthDuplicatedNodeFeatureDto): boolean {
        return (
            this.nodesDuplicationMap.has(editedNode.getOriginId()) &&
            this.nodesDuplicationMap
                .get(editedNode.getOriginId())
                .some((node: GtthDuplicatedNodeFeatureDto) => node.isMarkedAsUpdateOrigin())
        );
    }

    hasPotentialDuplicateRelatedToSameOrigin(editedNode: GtthDuplicatedNodeFeatureDto): boolean {
        return (
            this.nodesDuplicationMap.has(editedNode.getOriginId()) &&
            this.nodesDuplicationMap
                .get(editedNode.getOriginId())
                .some(
                    (node: GtthDuplicatedNodeFeatureDto) =>
                        this.stringDistance.getDistance(editedNode.getOriginName(), node.getName()) >= 0.85
                )
        );
    }

    hasDuplicateRelatedToOtherOrigin(editedNode: GtthDuplicatedNodeFeatureDto): boolean {
        let found = false;
        for (const nodes of this.nodesDuplicationMap.values()) {
            for (const node of nodes) {
                const hasTheSameId = node.getId() === editedNode.getId();
                const hasTheSameOrigin = editedNode.relatedOrigin && node.getOriginId() === editedNode.getOriginId();

                if (hasTheSameId && !hasTheSameOrigin && node.isMarkedAsUpdateOrigin()) {
                    found = true;
                    break;
                }
            }
            if (found) {
                break;
            }
        }
        return found;
    }

    checkIfNameValid(newName: string, editedNode: GtthDuplicatedNodeFeatureDto): boolean {
        let isValid = true;

        if (editedNode.isMarkedAsUpdateOrigin() && newName === editedNode.getOriginName()) {
            return true;
        }

        const originNames = this.historyService.logicalTopology.getAllStationsNames();
        if (originNames.includes(newName)) {
            return false;
        }

        for (const nodes of this.nodesDuplicationMap.values()) {
            for (const node of nodes) {
                if (node.getId() === editedNode.getId()) {
                    continue;
                }
                if (node.getName() === newName) {
                    isValid = false;
                    break;
                }
            }
            if (!isValid) {
                break;
            }
        }

        return isValid;
    }

    checkForNodesDuplications(duplicationDistance: number): Map<number, GtthDuplicatedNodeFeatureDto[]> {
        const nodesIdsMapping = new Map<number, number>();

        const nodesToCheck: GtthNodeFeatureDtoModel[] = [];

        this.nodesDuplicationMap = new Map<number, GtthDuplicatedNodeFeatureDto[]>();

        this.importedNodesMap.forEach((importedNode: GtthNodeFeatureDtoModel) => {
            const origin = this.historyService.logicalTopology.getStationByName(importedNode.getName());

            if (origin) {
                this.originFound(origin, importedNode, nodesIdsMapping);
            } else {
                this.originNotFound(importedNode, duplicationDistance, nodesToCheck);
            }
        });

        if (nodesToCheck.length) {
            nodesToCheck.forEach((node, i) => {
                this.checkDuplicatesInFile(node, i, nodesToCheck, duplicationDistance);
            });
        }

        if (nodesIdsMapping.size > 0) {
            this.changeLinksIds(nodesIdsMapping);
        }

        return this.nodesDuplicationMap;
    }

    public getFileDuplicates(): GtthDuplicatedNodeFeatureDto[][] {
        return this.fileDuplications;
    }

    duplicationsResolved(
        nodesDuplicationMap: Map<number, GtthDuplicatedNodeFeatureDto[]>,
        fileDuplications: GtthDuplicatedNodeFeatureDto[][]
    ) {
        const nodesIdsMapping = new Map<number, number>();

        if (nodesDuplicationMap?.size !== 0) {
            this.nodeDuplicationsResolved(nodesDuplicationMap, nodesIdsMapping);
        }

        if (fileDuplications?.length !== 0) {
            this.fileDuplicationsResolved(fileDuplications, nodesIdsMapping);
        }

        if (nodesIdsMapping.size > 0) {
            this.changeLinksIds(nodesIdsMapping);
        }
    }

    getNodesCount() {
        return this.importedNodesMap.size;
    }

    recalculateNodesByType() {
        this.nodesMap = new Map();
        Object.values(GtthImportNodeTypeEnum).forEach((enumValue) => this.nodesMap.set(enumValue as GtthImportNodeTypeEnum, []));
        this.losesToRemove = new Set();
        this.nodeTypesMap = new Map();
        this.movedConnectedNodes = new Set<number>();

        for (const node of this.importedNodesMap.values()) {
            let nodeType;

            const existentNode = this.historyService.logicalTopology.getStation(node.getId());

            if (!existentNode) {
                nodeType = GtthImportNodeTypeEnum.NEW;
            } else if (existentNode.toGeoJson().equals(node)) {
                nodeType = GtthImportNodeTypeEnum.NOT_CHANGED;
            } else if (existentNode.hasFreezeLinks()) {
                nodeType = GtthImportNodeTypeEnum.FROZEN_CHANGED;
            } else if (existentNode.linkIds.size > 0) {
                nodeType = GtthImportNodeTypeEnum.CONNECTED;
                if (!existentNode.getPosition().equals(GtthPosition.fromValues(node.getLng(), node.getLat()))) {
                    this.movedConnectedNodes.add(node.getId());
                    for (const los of existentNode.linkIds) {
                        this.losesToRemove.add(los);
                    }
                }
            } else {
                nodeType = GtthImportNodeTypeEnum.STANDALONE;
            }

            this.nodesMap.get(nodeType).push(node);
            this.nodeTypesMap.set(node.getId(), nodeType);
        }
    }

    recalculateLinksByType() {
        for (const link of this.importedLinks) {
            if (!this.nodeTypesMap.has(link.getNode0IdCustom()) || !this.nodeTypesMap.has(link.getNode1IdCustom())) {
                continue;
            }

            const linkCode = this.nodeTypesMap.get(link.getNode0IdCustom()) | this.nodeTypesMap.get(link.getNode1IdCustom());
            if (!this.linksMap?.has(linkCode)) {
                this.linksMap.set(linkCode, []);
            }
            const existingLine = this.historyService.logicalTopology.getLinkByNodeIds(link.getNode0IdCustom(), link.getNode1IdCustom());

            if (!existingLine) {
                this.linksMap.get(linkCode).push(link);
                continue;
            }

            if (existingLine.isUserForced()) {
                continue;
            }

            this.processForcedLink(link, existingLine, linkCode);
        }
    }

    getNewNodesCount(): number {
        return this.nodesMap.get(GtthImportNodeTypeEnum.NEW).length;
    }

    getStandaloneNodesCount(): number {
        return this.nodesMap.get(GtthImportNodeTypeEnum.STANDALONE).length;
    }

    getConnectedNodesCount(): number {
        return this.nodesMap.get(GtthImportNodeTypeEnum.CONNECTED).length;
    }

    getLinksCount() {
        return this.importedLinks.length;
    }

    nodesValueChanged(isCreateNewNodes: boolean, isUpdateStandalone: boolean, isUpdateConnectedNodes: boolean) {
        const nodesMask = this.calculateNodesMask(isCreateNewNodes, isUpdateStandalone, isUpdateConnectedNodes);

        this.createLinksCount = 0;
        this.createLosesCount = 0;

        this.linksMap.forEach((links, linkCode) => {
            if ((nodesMask & linkCode) === linkCode) {
                links.forEach((link) => (link.getIsUserForced() ? this.createLinksCount++ : this.createLosesCount++));
            }
        });
    }

    prepareCrudCommand(
        isCreateNewNodes: boolean,
        isUpdateStandalone: boolean,
        isUpdateConnectedNodes: boolean,
        isCreateLoses: boolean,
        isCreateLinks: boolean
    ): GtthCrudMultipleEntitiesCommand {
        const nodesToCreate: GtthNodeFeatureDtoModel[] = [];
        const nodesToUpdate: GtthNodeFeatureDtoModel[] = [];
        const losesIdToRemove = new Set<number>();
        const linksToCreate: GtthLinkFeatureDtoModel[] = [];

        if (isCreateNewNodes) {
            this.nodesMap.get(GtthImportNodeTypeEnum.NEW).forEach((node) => nodesToCreate.push(node));
        }

        if (isUpdateStandalone) {
            this.nodesMap.get(GtthImportNodeTypeEnum.STANDALONE).forEach((node) => nodesToUpdate.push(node));
        }

        if (isUpdateConnectedNodes) {
            this.nodesMap.get(GtthImportNodeTypeEnum.CONNECTED).forEach((node) => nodesToUpdate.push(node));
            this.losesToRemove.forEach((linkId) => losesIdToRemove.add(linkId));
        }

        const nodesMask = this.calculateNodesMask(isCreateNewNodes, isUpdateStandalone, isUpdateConnectedNodes);

        this.linksMap.forEach((links, linkCode) => {
            if ((nodesMask & linkCode) === linkCode) {
                links.forEach((link) => {
                    if ((link.getIsUserForced() && isCreateLinks) || (!link.getIsUserForced() && isCreateLoses)) {
                        const existentLink = this.historyService.logicalTopology.getLinkByNodeIds(
                            link.getNode0IdCustom(),
                            link.getNode1IdCustom()
                        );
                        if (existentLink) {
                            losesIdToRemove.add(existentLink.getId());
                        }
                        linksToCreate.push(link);
                    }
                });
            }
        });

        return this.historyService
            .command(GtthCrudMultipleEntitiesCommand)
            .createForCustomImport(nodesToCreate, nodesToUpdate, losesIdToRemove, linksToCreate);
    }

    getCreatedLosesCount() {
        return this.createLosesCount;
    }

    getCreatedLinksCount() {
        return this.createLinksCount;
    }

    getLoSesToRemoveCount() {
        return this.losesToRemove.size;
    }

    clearCounters() {
        this.linksMap = new Map<number, GtthLinkFeatureDtoModel[]>();
    }

    private originFound(origin: NodeFeature, importedNode: GtthNodeFeatureDtoModel, nodesIdsMapping: Map<number, number>): void {
        const originId = origin.getId();
        const distance = this.calculateDistanceBetween(origin, importedNode);

        if (distance > 0.1 || importedNode.getLayerIndex() !== origin.getLayerIndex()) {
            if (!this.nodesDuplicationMap.has(originId)) {
                this.nodesDuplicationMap.set(originId, []);
            }

            const duplicatedNodeFeatureDto = new GtthDuplicatedNodeFeatureDto(importedNode, origin);
            duplicatedNodeFeatureDto.setDistance(distance);

            if (origin.hasFreezeLinks()) {
                duplicatedNodeFeatureDto.setType(DuplicateTypeEnum.NOT_IMPORT);
            } else {
                duplicatedNodeFeatureDto.setType(DuplicateTypeEnum.UPDATE_ORIGIN);
            }
            this.nodesDuplicationMap.get(originId).unshift(duplicatedNodeFeatureDto);
        } else {
            nodesIdsMapping.set(importedNode.getId(), originId);
            importedNode.setId(originId);
        }
    }

    private originNotFound(
        importedNode: GtthNodeFeatureDtoModel,
        duplicationDistance: number,
        nodesToCheck: GtthNodeFeatureDtoModel[]
    ): void {
        const originNodesIterator = this.historyService.logicalTopology.getStationsIterator();

        let nodeHasOrigin = false;

        for (const originNode of originNodesIterator) {
            const originId = originNode.getId();
            const distance = this.calculateDistanceBetween(originNode, importedNode);

            if (importedNode.getLayerIndex() !== originNode.getLayerIndex() || distance > duplicationDistance) {
                continue;
            }

            nodeHasOrigin = true;

            const namesEquality = this.stringDistance.getDistance(originNode.getName(), importedNode.getName());

            if (namesEquality >= 0.85) {
                if (!this.nodesDuplicationMap.has(originId)) {
                    this.nodesDuplicationMap.set(originId, []);
                }

                const duplicatedNodeFeatureDto = new GtthDuplicatedNodeFeatureDto(importedNode, originNode);
                duplicatedNodeFeatureDto.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
                duplicatedNodeFeatureDto.setDistance(distance);
                this.nodesDuplicationMap.get(originId).push(duplicatedNodeFeatureDto);
            } else {
                if (!this.nodesDuplicationMap.has(originId)) {
                    this.nodesDuplicationMap.set(originId, []);
                }

                const duplicatedNodeFeatureDto = new GtthDuplicatedNodeFeatureDto(importedNode, originNode);
                duplicatedNodeFeatureDto.setType(DuplicateTypeEnum.NEW);
                duplicatedNodeFeatureDto.setDistance(distance);
                this.nodesDuplicationMap.get(originId).push(duplicatedNodeFeatureDto);
            }
        }
        if (!nodeHasOrigin) {
            nodesToCheck.push(importedNode);
        }
    }

    private nodeDuplicationsResolved(
        nodesDuplicationMap: Map<number, GtthDuplicatedNodeFeatureDto[]>,
        nodesIdsMapping: Map<number, number>
    ): void {
        for (const resolvedNodesArray of nodesDuplicationMap.values()) {
            for (const resolvedNode of resolvedNodesArray) {
                switch (resolvedNode.getType()) {
                    case DuplicateTypeEnum.NEW: {
                        const updatedNode = GtthNodeFeatureDtoModel.fromData(resolvedNode);
                        this.importedNodesMap.set(updatedNode.getId(), updatedNode);
                        break;
                    }
                    case DuplicateTypeEnum.NOT_IMPORT: {
                        nodesIdsMapping.set(resolvedNode.getId(), null);
                        this.importedNodesMap.delete(resolvedNode.getId());
                        break;
                    }
                    case DuplicateTypeEnum.POTENTIAL_DUPLICATE: {
                        this.mergeLinksOfDuplicatedNodes(resolvedNode, this.importedLinks);
                        this.importedNodesMap.delete(resolvedNode.getId());
                        break;
                    }
                    default: {
                        nodesIdsMapping.set(resolvedNode.getId(), resolvedNode.originId);
                        this.importedNodesMap.delete(resolvedNode.getId());
                        if (resolvedNode.useAsBaseConfig) {
                            resolvedNode.setId(resolvedNode.originId);
                            const updatedNode = GtthNodeFeatureDtoModel.fromData(resolvedNode);
                            this.importedNodesMap.set(resolvedNode.originId, updatedNode);
                        }
                    }
                }
            }
        }
    }

    private fileDuplicationsResolved(fileDuplications: GtthDuplicatedNodeFeatureDto[][], nodesIdsMapping: Map<number, number>): void {
        for (const fileNodesArray of fileDuplications) {
            for (const fileNode of fileNodesArray) {
                switch (fileNode.getType()) {
                    case DuplicateTypeEnum.NEW: {
                        const updatedNode = GtthNodeFeatureDtoModel.fromData(fileNode);
                        this.importedNodesMap.set(updatedNode.getId(), updatedNode);
                        break;
                    }
                    case DuplicateTypeEnum.NOT_IMPORT: {
                        const nodeId = fileNode.getId();
                        nodesIdsMapping.set(nodeId, null);
                        if (this.importedNodesMap.has(nodeId)) {
                            this.importedNodesMap.delete(nodeId);
                        }
                        break;
                    }
                    case DuplicateTypeEnum.POTENTIAL_DUPLICATE: {
                        const nodeId = fileNode.getId();
                        this.mergeLinksOfDuplicatedNodes(fileNode, this.importedLinks);
                        if (this.importedNodesMap.has(nodeId)) {
                            this.importedNodesMap.delete(nodeId);
                        }
                        break;
                    }
                    default:
                    //         do nothing
                }
            }
        }
    }

    private processForcedLink(link: GtthLinkFeatureDtoModel, existingLine: LinkFeature, linkCode: number): void {
        const targetLinkForced = link.getIsUserForced();

        const changedExisting =
            !existingLine.toGeoJson().notFrozenAttributesEquals(link) &&
            this.nodeTypesMap.get(link.getNode0IdCustom()) === GtthImportNodeTypeEnum.NOT_CHANGED &&
            this.nodeTypesMap.get(link.getNode1IdCustom()) === GtthImportNodeTypeEnum.NOT_CHANGED;

        const newBetweenChangedNodes =
            this.movedConnectedNodes.has(link.getNode0IdCustom()) || this.movedConnectedNodes.has(link.getNode1IdCustom());

        if (targetLinkForced || changedExisting || newBetweenChangedNodes) {
            if (link.getNode0IdCustom() !== existingLine.getFromId()) {
                link.setSectorApIdx(link.getSectorApIdx() === 0 ? 1 : 0);
            }

            link.setNode0IdCustom(existingLine.getFromId());
            link.setNode1IdCustom(existingLine.getToId());
            this.linksMap.get(linkCode).push(link);
        }
    }

    private checkDuplicatesInFile(
        searchedNode: GtthNodeFeatureDtoModel,
        nodeIndex: number,
        nodesArray: GtthNodeFeatureDtoModel[],
        duplicationDistance: number
    ) {
        // check: take first, check 2nd etc.
        const arrToRemove: number[] = [];
        const arrToSearchIn = nodesArray.slice(nodeIndex + 1);
        const newArr: GtthDuplicatedNodeFeatureDto[] = [];
        arrToRemove.push(nodeIndex);

        const duplicatedNodeFeatureDto = new GtthDuplicatedNodeFeatureDto(searchedNode, null);
        duplicatedNodeFeatureDto.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
        duplicatedNodeFeatureDto.setDistance(0);

        newArr.push(duplicatedNodeFeatureDto);

        arrToSearchIn.forEach((item: GtthNodeFeatureDtoModel, index: number) => {
            if (searchedNode.getLayerIndex() !== item.getLayerIndex()) {
                return;
            }

            const similarity = this.stringDistance.getDistance(searchedNode.getName(), item.getName());
            const distance = this.calculateDistanceBetweenNodes(searchedNode, item);

            if (similarity >= 0.85 && distance <= duplicationDistance) {
                const anotherDuplicatedNodeFeatureDto = new GtthDuplicatedNodeFeatureDto(item, null);
                anotherDuplicatedNodeFeatureDto.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
                anotherDuplicatedNodeFeatureDto.setDistance(distance);

                newArr.push(anotherDuplicatedNodeFeatureDto);
                arrToRemove.unshift(index + 1);
            }
        });
        if (newArr.length > 1) {
            this.fileDuplications.push(newArr);
            arrToRemove.forEach((index) => {
                nodesArray.splice(index, 1);
            });
        }
    }

    private calculateDistanceBetween(topologyNode: NodeFeature, importedNode: GtthNodeFeatureDtoModel) {
        const topologyNodePosition = topologyNode.getPosition().toLatLng(google);
        const importedNodePosition = new google.maps.LatLng(importedNode.getLat(), importedNode.getLng());
        return google.maps.geometry.spherical.computeDistanceBetween(topologyNodePosition, importedNodePosition);
    }

    private calculateDistanceBetweenNodes(node1: GtthNodeFeatureDtoModel, node2: GtthNodeFeatureDtoModel) {
        const node1Position = new google.maps.LatLng(node1.getLat(), node1.getLng());
        const node2Position = new google.maps.LatLng(node2.getLat(), node2.getLng());
        return google.maps.geometry.spherical.computeDistanceBetween(node1Position, node2Position);
    }

    private mergeLinksOfDuplicatedNodes(duplicatedNode: GtthDuplicatedNodeFeatureDto, linksArray: GtthLinkFeatureDtoModel[]): void {
        linksArray = linksArray.filter((link) => link.getNode1IdCustom() !== link.getNode0IdCustom());

        for (const link of linksArray) {
            if (link.getNode0IdCustom() === duplicatedNode.getId()) {
                link.setNode0IdCustom(duplicatedNode.getOriginId());
            }
            if (link.getNode1IdCustom() === duplicatedNode.getId()) {
                link.setNode1IdCustom(duplicatedNode.getOriginId());
            }
        }
    }

    private changeLinksIds(nodeIdsMapping: Map<number, number>) {
        this.importedLinks.forEach((link: GtthLinkFeatureDtoModel) => {
            if (nodeIdsMapping.has(link.getNode0IdCustom())) {
                link.setNode0IdCustom(nodeIdsMapping.get(link.getNode0IdCustom()));
            }
            if (nodeIdsMapping.has(link.getNode1IdCustom())) {
                link.setNode1IdCustom(nodeIdsMapping.get(link.getNode1IdCustom()));
            }
        });
    }

    private calculateNodesMask(isCreateNewNodes: boolean, isUpdateStandalone: boolean, isUpdateConnectedNodes: boolean): number {
        return (
            (isCreateNewNodes ? GtthImportNodeTypeEnum.NEW : 0) +
            (isUpdateStandalone ? GtthImportNodeTypeEnum.STANDALONE : 0) +
            (isUpdateConnectedNodes ? GtthImportNodeTypeEnum.CONNECTED : 0) +
            GtthImportNodeTypeEnum.NOT_CHANGED
        );
    }
}
